import { useEffect } from 'react'
import { useIsDesktop } from '@/hooks/use-is-desktop'
import { useRightRailStore } from '@/stores/right-rail-store'
import { CONFIG_RAIL_WIDTH } from './right-rail-constants'

/**
 * The docked column a panel config surface portals into. Sits in app-layout's
 * flex row next to the companion drawer, outside the sheet container for the
 * same reason (sheets must not cover it), and animates its width exactly like
 * the drawer so the two swap in place.
 *
 * The portal target stays mounted while closed (width 0, overflow hidden) so a
 * surface can claim the rail and render in the same commit. Desktop only: on
 * narrow viewports no container is registered and surfaces fall back to a
 * full-width sheet.
 *
 * Stacking: unlike the companion drawer (z-60, kept above sheet overlays so
 * chat stays usable), the rail carries NO z-index. Dialogs the rail opens
 * (metric editor, presets gallery, expression library) are body portals at
 * z-50 and must cover it; a modal sheet dimming the rail is the right
 * behavior for a config surface.
 */
export function RightRailHost() {
  const isDesktop = useIsDesktop()
  const activeId = useRightRailStore((s) => s.activeId)
  const setContainerEl = useRightRailStore((s) => s.setContainerEl)
  const isOpen = isDesktop && activeId != null

  useEffect(() => {
    if (!isDesktop) setContainerEl(null)
  }, [isDesktop, setContainerEl])

  return (
    <div
      data-right-rail
      role="complementary"
      aria-label="Panel configuration"
      className="relative flex shrink-0 flex-col overflow-hidden border-l border-border bg-background transition-[width] duration-300 ease-in-out"
      style={{ width: isOpen ? CONFIG_RAIL_WIDTH : 0, borderLeftWidth: isOpen ? 1 : 0 }}
    >
      {isDesktop && (
        <div
          ref={setContainerEl}
          className="relative flex h-full flex-col"
          style={{ width: CONFIG_RAIL_WIDTH }}
        />
      )}
    </div>
  )
}
